import { useEffect, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Avatar, Banner, Card, Chip, EmptyState, Spinner, cssVars, cx } from '../../design-system';
import { useAccess } from '../../hooks/useAccess';
import { useHomeViewModel } from './useHomeViewModel';
import { ROSTER_LIMIT } from './useAreaHomeSections';
import styles from './HomeScreen.module.css';
import { PageTitle } from '../../shell/PageTitle';

/**
 * Inicio — transcribed from the prototype's `data-screen-label="Inicio"` block.
 *
 * A dumb view. Every number, label and flag arrives already computed from
 * `useHomeViewModel`, so this file contains no data access, no date arithmetic
 * and no permission logic — it decides only what to draw. That separation is
 * what lets the same markup be exercised for four roles by handing the
 * view-model different props.
 */

export const HomeScreen = (): ReactNode => {
  const { holds } = useAccess();
  const location = useLocation();
  const navigate = useNavigate();
  const model = useHomeViewModel();

  // Set by the route guard when a permission redirect sent the user here.
  // Rendered as a banner rather than a toast so it survives the navigation.
  const denied =
    typeof location.state === 'object' &&
    location.state !== null &&
    'denied' in location.state &&
    typeof (location.state as { denied: unknown }).denied === 'string'
      ? (location.state as { denied: string }).denied
      : null;

  /**
   * Clears the message once it has been seen.
   *
   * History state outlives the navigation that set it, so without this the
   * notice would still be on screen after the user switched role and genuinely
   * gained access — telling them they are locked out of a section they can open.
   */
  useEffect(() => {
    if (denied === null) return;
    void navigate(location.pathname, { replace: true, state: null });
  }, [denied, navigate, location.pathname]);

  const canRequest = holds('solicitar');

  return (
    <div className="flow">
      {denied !== null && <Banner tone="warn">{denied}</Banner>}

      <PageTitle className={styles.title}>{model.greeting}</PageTitle>
      <p className={styles.subtitle}>{model.subtitle}</p>

      {/*
        Expiring balance. First in the visual order because it is the only card
        representing something the person will lose by not acting — which is why
        the design gives it a gradient and a left rail.
      */}
      {model.expiry !== null && canRequest && (
        <div className={styles.expiryBand}>
          <div className={styles.expiryCount}>
            <span className={styles.expiryNumber}>{model.expiry.days}</span>
            <span className={styles.expiryUnit}>
              {model.expiry.days === 1 ? 'día' : 'días'}
            </span>
          </div>
          <div className={styles.expiryText}>
            <p className={styles.expiryTitle}>{model.expiry.headline}</p>
            <p className={styles.expiryNote}>{model.expiry.note}</p>
          </div>
          <button type="button" className={styles.expiryAction} onClick={model.onScheduleExpiring}>
            Agendar esos días
          </button>
        </div>
      )}

      {model.stats.length > 0 && (
        <section className={cx(styles.panel, styles.statsPanel)}>
          <div className={styles.panelHead}>
            <h2 className={styles.panelTitle}>{model.statsTitle}</h2>
            <span className={styles.panelScope}>{model.statsScope}</span>
          </div>

          <div className={styles.statBand}>
            {model.stats.map((stat) => (
              <div key={`${stat.label}·${stat.chip}`} className={styles.statCell}>
                <div className={styles.statHead}>
                  <span
                    className={styles.statDot}
                    style={cssVars({ '--stat-color': stat.color })}
                    aria-hidden="true"
                  />
                  <span className={styles.statLabel}>{stat.label}</span>
                </div>
                <div className={styles.statFigure}>
                  <span className={styles.statValue}>{stat.value}</span>
                  <Chip
                    background={chipBackgroundFor(stat.label)}
                    foreground={chipForegroundFor(stat.label)}
                  >
                    {stat.chip}
                  </Chip>
                </div>
                <div className={styles.statNote}>{stat.note}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* The leader's queue. Above the two-column body because deciding on
          someone else's absence is time-sensitive in a way one's own is not. */}
      {holds('aprobar') && model.pendingApprovals.length === 0 && model.variant === 'leader' && (
        <section className={cx(styles.panel, styles.queuePanel)}>
          <div className={styles.panelHead}>
            <h2 className={styles.panelTitle}>Pendientes de tu aprobación</h2>
          </div>
          <EmptyState glyph="✓" title="Sin pendientes: tu bandeja está al día." note="" />
        </section>
      )}

      {holds('aprobar') && model.pendingApprovals.length > 0 && (
        <section className={cx(styles.panel, styles.queuePanel)}>
          <div className={styles.panelHead}>
            <div>
              <h2 className={styles.panelTitle}>Pendientes de tu aprobación</h2>
              <p className={styles.statNote}>
                Vacaciones, incapacidades y permisos en un solo lugar.
              </p>
            </div>
            {/* The card-level action opens the board plainly; only the rows carry
                a chip, because only a row knows which kind it is. */}
            <button
              type="button"
              className={styles.headAction}
              onClick={() => {
                model.onOpenBoard();
              }}
            >
              Abrir tablero
            </button>
          </div>

          <ul className={styles.rows}>
            {model.pendingApprovals.map((row) => (
              <li
                key={row.id}
                className={styles.row}
                style={cssVars({ '--row-accent': row.accentColor, '--row-kind-accent': row.kindAccent })}
              >
                <Avatar name={row.personName} color={row.accentColor} size={38} />
                <div className={styles.rowMain}>
                  <div className={styles.rowHeadline}>
                    <span className={styles.rowName}>{row.personName}</span>
                    <Chip background={row.kindBackground} foreground={row.kindForeground}>
                      {row.kind}
                    </Chip>
                    <Chip background={row.statusBackground} foreground={row.statusForeground}>
                      {row.status}
                    </Chip>
                  </div>
                  <div className={styles.rowMeta}>{row.meta}</div>
                </div>

                <div className={styles.rowActions}>
                  <button
                    type="button"
                    className={styles.approveButton}
                    onClick={() => {
                      model.onOpenBoard(row.board);
                    }}
                  >
                    {row.actionLabel}
                  </button>
                  <button
                    type="button"
                    className={styles.rejectButton}
                    onClick={() => {
                      model.onOpenBoard(row.board);
                    }}
                  >
                    Rechazar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/*
        The administrator's three panels. «Por atender hoy» leads because every
        row in it is something that blocks other people from working — an
        unvalidated account cannot request days, a person with no leader has
        nobody to escalate to.
      */}
      {model.variant === 'admin' && (
        <>
          {model.area.todo.length > 0 && (
            <section className={cx(styles.panel, styles.todoPanel)}>
              <div className={styles.panelHead}>
                <div>
                  <h2 className={styles.panelTitle}>Por atender hoy</h2>
                  <p className={styles.panelNote}>{model.area.todoNote}</p>
                </div>
                <Chip background="var(--color-brand-bg-alt)" foreground="var(--color-brand-ink)">
                  {model.area.todoTag}
                </Chip>
              </div>

              <div className={styles.todoGrid}>
                {model.area.todo.map((row) => (
                  <div
                    key={row.id}
                    className={styles.todoCard}
                    style={cssVars({ '--todo-color': row.color })}
                  >
                    <div className={styles.todoHead}>
                      <span className={styles.todoCount}>{row.count}</span>
                      <div className={styles.todoText}>
                        <span className={styles.todoTitle}>{row.title}</span>
                        <span className={styles.todoNote}>{row.note}</span>
                      </div>
                    </div>
                    <button type="button" className={styles.todoAction} onClick={row.onOpen}>
                      {row.ctaLabel}
                    </button>
                  </div>
                ))}
              </div>

              {/* Stated rather than hidden: two of the counts above are computed
                  from a capped roster read, and a silent cap reads as "all". */}
              {model.area.rosterTruncated && (
                <p className={styles.panelFootnote}>
                  El área supera el máximo que revisamos de una vez, así que «personas sin líder» y
                  «saldo en riesgo» cuentan sólo a los primeros {ROSTER_LIMIT}. Reportes tiene el
                  detalle completo.
                </p>
              )}
            </section>
          )}

          <div className={cx(styles.grid, styles.adminGrid)}>
            {model.area.coverage.length > 0 && (
              <section className={styles.panel}>
                <div className={styles.panelHead}>
                  <h2 className={styles.panelTitle}>Cobertura por sub área</h2>
                  <button type="button" className={styles.headAction} onClick={model.onOpenAdmin}>
                    Ajustar topes
                  </button>
                </div>

                <ul className={styles.coverageList}>
                  {model.area.coverage.map((row) => (
                    <li key={row.id} className={styles.coverageRow}>
                      <div className={styles.coverageHead}>
                        <span
                          className={styles.coverageDot}
                          style={cssVars({ '--cov-color': row.color })}
                          aria-hidden="true"
                        />
                        <span className={styles.coverageName}>{row.name}</span>
                        <Chip background={row.tagBackground} foreground={row.tagForeground}>
                          {row.tagLabel}
                        </Chip>
                      </div>
                      <div className={styles.coverageMeta}>{row.meta}</div>
                      <div className={styles.coverageTrack}>
                        <div
                          className={styles.coverageBar}
                          style={cssVars({
                            '--cov-width': `${String(Math.max(4, row.percent))}%`,
                            '--cov-bar': row.barColor,
                          })}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <div className={styles.column}>
              <section className={styles.panel}>
                <div className={styles.panelHead}>
                  <h2 className={styles.panelTitle}>Próximas ausencias</h2>
                  <button
                    type="button"
                    className={styles.headAction}
                    onClick={model.onOpenCalendar}
                  >
                    Ver calendario
                  </button>
                </div>

                {model.area.upcoming.length === 0 ? (
                  <EmptyState
                    glyph="▦"
                    title="Sin ausencias próximas"
                    note="Nadie tiene ausencias agendadas en los próximos 45 días."
                  />
                ) : (
                  <ul className={styles.outList}>
                    {model.area.upcoming.map((row) => (
                      <li key={row.id} className={styles.outRow}>
                        <Avatar name={row.name} color={row.color} size={34} />
                        <span className={styles.outText}>
                          <span className={styles.outName}>{row.name}</span>
                          <span className={styles.outSub}>
                            {row.subAreaName} · {row.rangeLabel}
                          </span>
                        </span>
                        <Chip
                          background={
                            row.soon ? 'var(--color-warning-bg)' : 'var(--color-surface-subtle-alt)'
                          }
                          foreground={
                            row.soon ? 'var(--color-warning-ink)' : 'var(--color-text-tertiary-alt)'
                          }
                        >
                          {row.whenLabel}
                        </Chip>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          </div>
        </>
      )}

      {/*
        The superuser's four panels. None of the personal home applies to them —
        they hold no balance and file no requests — so this replaces the body
        rather than adding to it.
      */}
      {model.variant === 'super' && (
        <>
          <div className={cx(styles.grid, styles.systemGrid)}>
            <section className={cx(styles.panel, styles.systemPanel)}>
              <div className={styles.panelHead}>
                <h2 className={styles.panelTitle}>Áreas de la empresa</h2>
                <button type="button" className={styles.headAction} onClick={model.onOpenConfig}>
                  Administrar
                </button>
              </div>

              <ul className={styles.coverageList}>
                {model.system.areas.map((row) => (
                  <li key={row.id} className={styles.coverageRow}>
                    <div className={styles.coverageHead}>
                      <span
                        className={styles.coverageDot}
                        style={cssVars({ '--cov-color': row.color })}
                        aria-hidden="true"
                      />
                      <span className={styles.coverageName}>{row.name}</span>
                      <Chip
                        background={
                          row.active ? 'var(--color-success-bg)' : 'var(--color-surface-subtle-alt)'
                        }
                        foreground={
                          row.active
                            ? 'var(--color-success-ink)'
                            : 'var(--color-text-tertiary-alt)'
                        }
                      >
                        {row.stateLabel}
                      </Chip>
                    </div>
                    <div className={styles.coverageMeta}>{row.meta}</div>
                    <div className={styles.coverageTrack}>
                      <div
                        className={styles.coverageBar}
                        style={cssVars({
                          '--cov-width': `${String(Math.max(4, row.percent))}%`,
                          '--cov-bar': row.active ? row.color : 'var(--color-text-tertiary)',
                        })}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </section>

            <div className={styles.systemColumn}>
              <section className={cx(styles.panel, styles.systemPanel)}>
                <div className={styles.panelHead}>
                  <h2 className={styles.panelTitle}>Features por módulo</h2>
                  <button type="button" className={styles.headAction} onClick={model.onOpenConfig}>
                    Configurar
                  </button>
                </div>

                <ul className={styles.featureList}>
                  {model.system.features.map((feature) => (
                    <li key={feature.key} className={styles.featureRow}>
                      <span
                        className={styles.featureDot}
                        style={cssVars({
                          '--feat-color': feature.on
                            ? feature.color
                            : 'var(--color-text-tertiary)',
                        })}
                        aria-hidden="true"
                      />
                      <span className={styles.featureLabel}>{feature.label}</span>
                      <Chip
                        background={
                          feature.on
                            ? 'var(--color-success-bg)'
                            : 'var(--color-surface-subtle-alt)'
                        }
                        foreground={
                          feature.on
                            ? 'var(--color-success-ink)'
                            : 'var(--color-text-tertiary-alt)'
                        }
                      >
                        {feature.on ? 'Activa' : 'Inactiva'}
                      </Chip>
                    </li>
                  ))}
                </ul>
              </section>
            </div>
          </div>

          <div className={cx(styles.grid, styles.systemGrid)}>
            <section className={cx(styles.panel, styles.systemPanel)}>
              <div className={styles.panelHead}>
                <h2 className={styles.panelTitle}>Salud de la plataforma</h2>
              </div>

              <ul className={styles.healthList}>
                {model.system.health.map((row) => (
                  <li key={row.id} className={styles.healthRow}>
                    <span
                      className={styles.healthDot}
                      style={cssVars({
                        '--health-color': row.ok
                          ? 'var(--color-success)'
                          : 'var(--color-warning)',
                      })}
                      aria-hidden="true"
                    />
                    <span className={styles.healthLabel}>{row.label}</span>
                    <span className={styles.healthValue}>{row.value}</span>
                  </li>
                ))}
              </ul>
            </section>

            <div className={styles.systemColumn}>
              <section className={cx(styles.panel, styles.systemPanel)}>
                <div className={styles.panelHead}>
                  <h2 className={styles.panelTitle}>Últimos cambios de configuración</h2>
                  <button type="button" className={styles.headAction} onClick={model.onOpenLog}>
                    Ver bitácora
                  </button>
                </div>

                {model.system.log.length === 0 ? (
                  <EmptyState
                    glyph="§"
                    title="Sin cambios recientes"
                    note="Los cambios de configuración de toda la empresa aparecerán aquí."
                  />
                ) : (
                  <ul className={styles.logList}>
                    {model.system.log.map((entry) => (
                      <li key={entry.id} className={styles.logRow}>
                        <span
                          className={styles.logIcon}
                          style={cssVars({ '--log-color': entry.color })}
                          aria-hidden="true"
                        >
                          {entry.glyph}
                        </span>
                        <span className={styles.logText}>
                          <span className={styles.logAction}>{entry.action}</span>
                          <span className={styles.logMeta}>{entry.meta}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          </div>

          {model.tip !== null && (
            <div className={styles.tipCard}>
              <div className={styles.tipLabel}>Recomendación del sistema</div>
              <p className={styles.tipText}>{model.tip}</p>
            </div>
          )}
        </>
      )}

      {/* The personal body. A superuser has none — no balance, no requests, no
          area whose absences would mean anything to them. */}
      <div
        className={cx(
          styles.grid,
          model.variant === 'super' && styles.gridHidden,
          model.variant === 'admin' && styles.adminOutGrid,
        )}
      >
        {model.myRequests.length > 0 && (
          <section className={styles.panel}>
            <div className={styles.panelHead}>
              <h2 className={styles.panelTitle}>Mis solicitudes</h2>
              <button type="button" className={styles.headAction} onClick={model.onOpenRequests}>
                Ver todas
              </button>
            </div>

            <ul className={styles.recentList}>
              {model.myRequests.map((request) => (
                <li
                  key={request.id}
                  className={styles.recentRow}
                  style={cssVars({
                    '--date-bg': request.statusBackground,
                    '--date-fg': request.statusForeground,
                    '--date-accent': request.statusAccent,
                  })}
                >
                  <div className={styles.dateBox}>
                    <span className={styles.dateMonth}>{request.month}</span>
                    <span className={styles.dateDay}>{request.day}</span>
                  </div>

                  <div>
                    <div className={styles.recentRange}>{request.rangeLabel}</div>
                    <div className={styles.recentMeta}>{request.meta}</div>

                    <div
                      className={styles.trail}
                      role="img"
                      aria-label={`Avance: ${request.steps
                        .filter((s) => s.state !== 'pending')
                        .map((s) => s.label)
                        .join(', ')}`}
                    >
                      {request.steps.map((step) => (
                        <span
                          key={step.label}
                          className={cx(
                            styles.trailSegment,
                            step.state !== 'pending' && styles.trailDone,
                          )}
                          style={cssVars({ '--seg-color': request.statusAccent })}
                          title={step.label}
                        />
                      ))}
                    </div>
                  </div>

                  <Chip
                    background={request.statusBackground}
                    foreground={request.statusForeground}
                  >
                    {request.status}
                  </Chip>
                </li>
              ))}
            </ul>
          </section>
        )}

        {model.outThisWeek.length > 0 && (
          <div className={styles.column}>
            <section className={styles.panel}>
              <div className={styles.panelHead}>
                <h2 className={styles.panelTitle}>Fuera esta semana</h2>
              </div>

              <ul className={styles.outList}>
                {model.outThisWeek.map((person) => (
                  <li key={person.id} className={styles.outRow}>
                    <Avatar name={person.name} color={person.color} size={38} />
                    <span className={styles.outText}>
                      <span className={styles.outName}>{person.name}</span>
                      <span className={styles.outSub}>
                        {person.subAreaName}
                        {/* A quién buscar en su lugar. Ver `replacementName`. */}
                        {person.replacementName.length > 0 && (
                          <>
                            {' · cubre '}
                            <strong className={styles.outCover}>
                              {person.replacementName}
                            </strong>
                          </>
                        )}
                      </span>
                    </span>
                    <span className={styles.outRange}>{person.rangeLabel}</span>
                  </li>
                ))}
              </ul>

              <button type="button" className={styles.outAction} onClick={model.onOpenCalendar}>
                Ver calendario del área
              </button>
            </section>

            {model.tip !== null && (
              <div className={styles.tipCard}>
                <div className={styles.tipLabel}>Sugerencia</div>
                <p className={styles.tipText}>{model.tip}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {model.loading && (
        <Card>
          <Spinner label="Cargando tu inicio" />
        </Card>
      )}

      {!model.loading &&
        model.variant !== 'super' &&
        model.variant !== 'admin' &&
        model.stats.length === 0 &&
        model.myRequests.length === 0 &&
        model.pendingApprovals.length === 0 && (
          <Card>
            <EmptyState
              glyph="⌂"
              title="Nada que mostrar todavía"
              note="Cuando haya solicitudes o ausencias en tu área aparecerán aquí."
            />
          </Card>
        )}

      {model.error !== null && <Banner tone="danger">{model.error}</Banner>}
    </div>
  );
};

const chipBackgroundFor = (label: string): string => {
  if (label === 'Tomados' || label === 'Fuera hoy') return 'var(--color-success-bg)';
  if (
    label === 'En revisión' ||
    label === 'Módulos apagados' ||
    label === 'Cuentas por validar'
  ) {
    return 'var(--color-warning-bg)';
  }
  if (label === 'Feriados activos') return 'var(--color-violet-bg)';
  if (label === 'Features activas') return 'var(--color-violet-bg)';
  return 'var(--color-brand-bg)';
};

const chipForegroundFor = (label: string): string => {
  if (label === 'Tomados' || label === 'Fuera hoy') return 'var(--color-success-ink)';
  if (
    label === 'En revisión' ||
    label === 'Módulos apagados' ||
    label === 'Cuentas por validar'
  ) {
    return 'var(--color-warning-ink)';
  }
  if (label === 'Feriados activos') return 'var(--color-violet-ink)';
  if (label === 'Features activas') return 'var(--color-violet-ink)';
  return 'var(--color-brand-ink)';
};
